import { useCallback } from 'react';
import { useMutation, useQuery } from 'react-query';
import { getAPI } from '../../services/API';
import { queryClient } from '../../services/queryClient';
import { sdkErrorHandler } from '../../utils';
import useRequestConfig from './useRequestConfig';

export const QUERY_KEY_FAVOURITE_GAMES = 'favourite-games';

const FAVOURITE_GAMES_QUERY_OPTIONS = {
  cacheTime: 0,
  // because of native channels
  refetchOnWindowFocus: 'always',
};

export const useQueryFavouriteGames = (queryOptions = {}, productType) => {
  const requestConfig = useRequestConfig();

  const fetch = ({ signal }) =>
    getAPI()
      .games.getFavourites({ signal, productType, ...requestConfig })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          queryOptions,
          productType,
          source: `SDK API games.getFavourites`,
        })
      );

  return useQuery(
    [QUERY_KEY_FAVOURITE_GAMES, productType, requestConfig.headers],
    fetch,
    {
      ...FAVOURITE_GAMES_QUERY_OPTIONS,
      ...queryOptions,
    }
  );
};

export const useMutationAddGameToFavourite = (
  gameId,
  queryOptions = {},
  productType
) => {
  const requestConfig = useRequestConfig();

  const fetch = () =>
    getAPI()
      .games.addFavourite(gameId, { ...requestConfig, productType })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          queryOptions,
          productType,
          gameId,
          source: `SDK API games.addFavourite`,
        })
      );

  const onSuccess = useCallback(async () => {
    await queryClient.refetchQueries(QUERY_KEY_FAVOURITE_GAMES);
    if (queryOptions.onSuccess) {
      queryOptions.onSuccess?.();
    }
  }, [queryOptions]);

  return useMutation(fetch, { ...queryOptions, onSuccess });
};

export const useMutationRemoveGameToFavourite = (
  gameId,
  queryOptions = {},
  productType
) => {
  const requestConfig = useRequestConfig();

  const fetch = () =>
    getAPI().games.removeFavourite(gameId, {
      body: {}, // Be attention! Empty body is required. It needs for fix issue https://jira.pm.tech/browse/GAM-11430
      productType,
      ...requestConfig,
    });

  const onSuccess = useCallback(async () => {
    await queryClient.refetchQueries(QUERY_KEY_FAVOURITE_GAMES);
    if (queryOptions.onSuccess) {
      queryOptions.onSuccess?.();
    }
  }, [queryOptions]);

  return useMutation(fetch, { ...queryOptions, onSuccess });
};
